import { z } from 'zod';
/**
 * Idiomas: um módulo do Secretário para aprender uma língua, não um app de idiomas.
 * Teste de nível adaptativo (CEFR), duas metodologias à escolha (conversa com
 * propósito; trilha com repetição espaçada), aula do dia curta, caderno de erros,
 * ritmo da semana (sem ofensiva nem culpa) e reteste depois de 30 dias.
 */
export const LANGUAGES = ['en', 'es', 'fr', 'it', 'de'];
export const LANGUAGE_INFO = {
    en: { name: 'Inglês', adjective: 'inglês', locale: 'en-US', available: true },
    es: { name: 'Espanhol', adjective: 'espanhol', locale: 'es-ES', available: false },
    fr: { name: 'Francês', adjective: 'francês', locale: 'fr-FR', available: false },
    it: { name: 'Italiano', adjective: 'italiano', locale: 'it-IT', available: false },
    de: { name: 'Alemão', adjective: 'alemão', locale: 'de-DE', available: false },
};
export const languageKeySchema = z.enum(LANGUAGES);
export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2'];
export const cefrLevelSchema = z.enum(CEFR_LEVELS);
export const CEFR_LABEL = {
    A1: 'Começando',
    A2: 'Básico',
    B1: 'Intermediário',
    B2: 'Avançado',
};
export const METHODS = ['conversa', 'trilha'];
export const methodSchema = z.enum(METHODS);
export const METHOD_INFO = {
    conversa: {
        name: 'Conversa com propósito',
        line: 'Eu encenho situações reais (hotel, restaurante, reunião) no seu nível e corrijo com leveza.',
    },
    trilha: {
        name: 'Trilha com repetição',
        line: 'Lições curtas por nível, palavras que voltam no dia certo e revisão semanal.',
    },
};
/** Pergunta do teste de nível (e das lições de escolha). */
export const languageQuestionSchema = z.object({
    id: z.string().max(40),
    level: cefrLevelSchema,
    prompt: z.string().max(240),
    options: z.array(z.string().max(80)).min(2).max(4),
    /** Índice da certa (só na lição; no teste fica no servidor). */
    answer: z.number().int().min(0).max(3).optional(),
});
export const placementStateSchema = z.object({
    /** Quantas já foram respondidas e quantas são. */
    answered: z.number().int(),
    total: z.number().int(),
    question: languageQuestionSchema.nullable(),
    /** Resultado, quando o teste acabou. */
    level: cefrLevelSchema.nullable(),
});
export const exerciseSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('escolha'),
        prompt: z.string().max(240),
        options: z.array(z.string().max(80)).min(2).max(4),
        answer: z.number().int().min(0).max(3),
        hint: z.string().max(200).optional(),
    }),
    z.object({
        kind: z.literal('completar'),
        /** Frase com ___ no lugar da palavra. */
        prompt: z.string().max(240),
        answer: z.string().max(60),
        hint: z.string().max(200).optional(),
    }),
    z.object({
        kind: z.literal('ouvir'),
        /** O que a voz diz (no idioma); a pessoa escreve o que ouviu. */
        say: z.string().max(200),
        answer: z.string().max(200),
        hint: z.string().max(200).optional(),
    }),
    z.object({
        kind: z.literal('falar'),
        /** O que a pessoa deve dizer (no idioma). */
        say: z.string().max(200),
        meaning: z.string().max(200),
    }),
]);
export const vocabSchema = z.object({
    word: z.string().max(60),
    meaning: z.string().max(120),
    example: z.string().max(200),
});
export const lessonSchema = z.object({
    id: z.string().max(40),
    level: cefrLevelSchema,
    title: z.string().max(120),
    intro: z.string().max(400),
    vocab: z.array(vocabSchema).max(10),
    exercises: z.array(exerciseSchema).max(10),
});
export const scenarioSchema = z.object({
    id: z.string().max(40),
    level: cefrLevelSchema,
    title: z.string().max(120),
    /** A situação, em português, para a pessoa saber o que vai acontecer. */
    situation: z.string().max(400),
    /** O que ela precisa conseguir (em português). */
    goals: z.array(z.string().max(120)).max(4),
    /** A primeira fala do Secretário, no idioma. */
    opening: z.string().max(300),
    /** Quem o Secretário interpreta. */
    role: z.string().max(80),
});
export const flashcardSchema = z.object({
    id: z.string().max(40),
    word: z.string().max(60),
    meaning: z.string().max(120),
    example: z.string().max(200),
    /** Quando volta (AAAA-MM-DD). */
    due: z.string(),
    /** Acertos seguidos espaçados; 3+ conta como "sua". */
    streak: z.number().int(),
});
export const courseSchema = z.object({
    language: languageKeySchema,
    level: cefrLevelSchema.nullable(),
    method: methodSchema.nullable(),
    placedAt: z.iso.datetime().nullable(),
    /** Meta de dias por semana (ritmo), nunca ofensiva. */
    weekGoal: z.number().int().min(1).max(7),
    lessonsDone: z.number().int(),
    /** Dias praticados nesta semana (AAAA-MM-DD). */
    weekDays: z.array(z.string()).max(7),
    /** Cartões que voltam hoje. */
    cardsDue: z.number().int(),
    /** Palavras "suas" (3 acertos espaçados). */
    wordsKnown: z.number().int(),
    cardsTotal: z.number().int(),
    /** Erros anotados (frases curtas, em português). */
    notebook: z.array(z.string().max(200)).max(30),
    lastLessonAt: z.iso.datetime().nullable(),
    /** Reteste sugerido a partir desta data. */
    retestAt: z.iso.datetime().nullable(),
    /** Nível anterior, quando já houve reteste. */
    previousLevel: cefrLevelSchema.nullable(),
});
export const courseListSchema = z.object({ courses: z.array(courseSchema) });
export const startCourseSchema = z.object({
    method: methodSchema,
    weekGoal: z.number().int().min(1).max(7).default(3),
});
export const placementAnswerSchema = z.object({
    questionId: z.string().max(40),
    answer: z.number().int().min(0).max(3),
});
/** A aula do dia: trilha (lição) ou conversa (cenário). */
export const lessonOfDaySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('trilha'),
        lesson: lessonSchema,
        cards: z.array(flashcardSchema).max(20),
    }),
    z.object({ kind: z.literal('conversa'), scenario: scenarioSchema }),
]);
export const finishLessonSchema = z.object({
    lessonId: z.string().max(40),
    /** Acertos e total dos exercícios (trilha). */
    correct: z.number().int().min(0).max(50).optional(),
    total: z.number().int().min(0).max(50).optional(),
    /** Erros para o caderno (frases curtas). */
    mistakes: z.array(z.string().max(200)).max(10).default([]),
    /** Palavras novas da lição que entram nos cartões. */
    learned: z.array(vocabSchema).max(10).default([]),
});
export const cardAnswerSchema = z.object({
    cardId: z.string().max(40),
    remembered: z.boolean(),
});
export const conversationTurnSchema = z.object({
    scenarioId: z.string().max(40),
    /** Falas anteriores (até 12), para o Secretário continuar a cena. */
    history: z
        .array(z.object({ role: z.enum(['owner', 'tutor']), text: z.string().max(600) }))
        .max(12)
        .default([]),
    text: z.string().trim().min(1).max(600),
});
export const conversationReplySchema = z.object({
    /** A fala do personagem, no idioma. */
    reply: z.string().max(600),
    /** Correção curta, em português, quando houve erro (null se estava bom). */
    correction: z.string().max(300).nullable(),
    /** Tradução da fala, para quem travou. */
    translation: z.string().max(600).nullable(),
    /** A cena cumpriu os objetivos. */
    done: z.boolean(),
});
/** Ferramentas da conversa (abrem a página; a aula acontece nela). */
export const LANGUAGE_OPEN_TOOL = 'abrir_idiomas';
export const LANGUAGE_PROGRESS_TOOL = 'progresso_no_idioma';
export const ASSISTANT_CAP_LANGUAGES = 'idiomas';
